import React, { useEffect, useRef } from "react";
import { TimePoint } from "../types";
import "../styles/EventGallery.css";

// Timeline component
const Timeline: React.FC<{
  timePoints: TimePoint[];
  onTimeClick: (position: number) => void;
  currentDateTime: string;
  showFullDate?: boolean;
}> = ({ timePoints, onTimeClick, currentDateTime, showFullDate = false }) => {
  const timeMarkersRef = useRef<HTMLDivElement>(null);
  const activeMarkerRef = useRef<HTMLDivElement | null>(null);

  // Auto-scroll to keep active time marker visible
  useEffect(() => {
    if (timeMarkersRef.current && activeMarkerRef.current) {
      const container = timeMarkersRef.current;
      const activeMarker = activeMarkerRef.current;

      // Calculate the scroll position to center the active marker
      const containerHeight = container.clientHeight;
      const markerTop = activeMarker.offsetTop;
      const markerHeight = activeMarker.clientHeight;
      const scrollPosition = markerTop - containerHeight / 2 + markerHeight / 2;

      // Smooth scroll to the position
      container.scrollTo({
        top: scrollPosition,
        behavior: "smooth",
      });
    }
  }, [currentDateTime]);

  const currentDateTimeObj = new Date(currentDateTime);
  // Set current time
  const currentTime = currentDateTimeObj.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false, // Use 24-hour format
    timeZone: "Europe/Warsaw",
  });
  const currentDate = currentDateTimeObj.toLocaleDateString(undefined, {
    month: "numeric",
    day: "numeric",
    timeZone: "Europe/Warsaw",
  });

  return (
    <div className="timeline">
      <div className="current-time">
        {showFullDate && currentDate ? (
          <span className="current-date-time">
            {currentDate}, {currentTime}
          </span>
        ) : (
          currentTime
        )}
      </div>
      <div className="time-markers" ref={timeMarkersRef}>
        {timePoints.map((point) => {
          // For markers, we can optionally extract dates for display when spanning multiple days
          const dateTime = point.fullDateTime.split(" "); // [YYYY-MM-DD, HH:MM]
          const timeDisplay = point.time;

          // Check if this marker corresponds to the current time
          const isActive =
            point.isoDateTime &&
            currentDateTime &&
            new Date(point.isoDateTime).toISOString().substring(0, 13) ===
              new Date(currentDateTime).toISOString().substring(0, 13); // Compare up to hours

          return (
            <div
              key={point.fullDateTime}
              className={`time-marker ${isActive ? "active" : ""}`}
              ref={isActive ? activeMarkerRef : null}
              onClick={() => onTimeClick(point.position)}
            >
              {showFullDate && (
                <div className="date-label">
                  {new Date(dateTime[0]).toLocaleDateString(undefined, {
                    month: "numeric",
                    day: "numeric",
                    timeZone: "Europe/Warsaw",
                  })}
                </div>
              )}
              <div className="time-label">{timeDisplay}</div>
              <div className="marker-line"></div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default Timeline;
